/**
 * All about my dad — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const allAboutMyDadSheetsPack: ToilePack = {
    slug: 'all-about-my-dad-sheets',
    category: 'season',
    name: 'All about my dad',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'questions', label: 'Question sheet', subject: 'a page headed "All About My Dad" with an empty square frame labeled "This is my dad" and prompts with blank lines: "His name is", "He is ___ years old", "His favorite food is", "He is really good at"'},
        {key: 'interview', label: 'Dad interview', subject: 'a page headed "Interview with My Dad" with five numbered questions such as "What was your first job?" and "What makes you happy?", two ruled answer lines under each'},
        {key: 'award', label: 'Dad award', subject: 'a certificate-style page headed "World\'s Best Dad Award" with a rosette ribbon, a blank line "Awarded to" and a blank line "Because"'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolor illustration, loose edges, hand-lettered text', palette: 'navy, forest green and tan washes on white paper', framing: 'Centered on the page with a wide white margin'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'navy, orange, sky blue and cream, a limited palette', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'lineart', label: 'Line art', style: 'fine black line art, uniform thin stroke, closed shapes, no fills, simple hand lettering', palette: 'black ink only', framing: 'Drawn on plain white paper with a clean margin'},
    ],
    directions: ['Spanish', 'Sports', 'Tool border'],
    directionHint: 'try: Spanish',
    seo: {
        title: 'All About My Dad Printable: Questions, Interview, Award',
        metaDescription: "Printable All About My Dad sheets for Father's Day: a question page, a dad interview and an award page, in three styles.",
        h1: 'All about my dad: questions, interview, dad award',
        tagline: 'Nine fill-in sheets with a titled header, a frame to draw in and lines to write on, in three styles.',
        intro: "A fill-in sheet a teacher or parent hands a child before Father's Day. The child writes what their dad likes, answers an interview with five numbered questions, or fills in an award certificate, and draws him in the frame. Each sheet has a titled header and empty lines for the answers. Watercolor, flat color and line art styles. Type a different heading or language, such as Spanish, and the same layout carries it.",
        useCases: [
            "a classroom activity before Father's Day",
            'a family keepsake a child fills in',
            'a school family-week homework page',
            "a page to give with a Father's Day card",
        ],
        faq: [
            {q: 'What is on the award sheet?', a: 'A rosette-ribbon certificate headed with an award title, with blank lines for Awarded to and Because.'},
            {q: 'Is there space to draw?', a: 'Yes. The question sheet has an empty square frame labelled This is my dad.'},
            {q: 'Can the language change?', a: 'Yes. Type a different language, such as Spanish, and the same layout carries it.'},
        ],
    },
}
